/**
 * Descriptors for the primary (left) toolbar row — Home, New, Explorer,
 * Terminal, AI ▾, Overview, More ▾.
 *
 * Every label, tooltip, testid and class here is a verbatim carry-over from
 * the hand-written JSX this replaced; `MainToolbar.primaryRowParity.test.tsx`
 * pins the rendered result. `ai` and `more` render their own components, so
 * they carry no `run` — they exist as descriptors purely so B14 can reorder
 * and (for AI) hide them as atomic slots.
 */
import { FileCode, Home, LayoutDashboard, LayoutGrid, MoreHorizontal, Table, Terminal } from 'lucide-react';
import { toolbarTooltips } from '../../toolbar-context';
import {
  queryEditorUnsupportedReason,
  tableExplorerUnsupportedReason,
} from './toolbarEngineFilters';
import type { ToolbarItemDescriptor, ToolbarModelDeps } from './toolbarItemTypes';

export function buildPrimaryToolbarItems({ gating, actions }: ToolbarModelDeps): ToolbarItemDescriptor[] {
  const { contextState } = gating;

  return [
    {
      id: 'home',
      render: 'button',
      label: 'Home',
      testId: 'home-button',
      icon: Home,
      tooltip: 'Open Start Page (Ctrl+Shift+H)',
      iconOnly: true,
      className: 'bg-primary text-primary-foreground hover:bg-primary/90',
      anchor: 'first',
      hideable: false,
      commandId: 'nav:go-to-home',
      run: actions.home,
    },
    {
      id: 'new-query',
      render: 'button',
      label: 'New',
      testId: 'new-query-button',
      icon: FileCode,
      tooltip: gating.queryEditorUnsupported
        ? queryEditorUnsupportedReason(gating.selectedConnectionEngine)
        : toolbarTooltips.newQuery(contextState),
      disabled: gating.queryEditorUnsupported,
      className: gating.queryEditorUnsupported
        ? 'opacity-50 cursor-not-allowed'
        : gating.firstConnectCoachVisible
          ? 'animate-first-connect-pulse'
          : undefined,
      commandId: 'workspace:new-query',
      run: actions['new-query'],
    },
    {
      id: 'table-explorer',
      render: 'button',
      label: 'Explorer',
      testId: 'table-explorer-button',
      icon: Table,
      tooltip: gating.tableExplorerUnsupported
        ? tableExplorerUnsupportedReason(gating.selectedConnectionEngine)
        : toolbarTooltips.tableExplorer(contextState),
      disabled: gating.tableExplorerUnsupported,
      className: gating.tableExplorerUnsupported ? 'opacity-50 cursor-not-allowed' : undefined,
      commandId: 'workspace:table-explorer',
      run: actions['table-explorer'],
    },
    {
      id: 'terminal',
      render: 'button',
      label: 'Terminal',
      testId: 'terminal-button',
      icon: Terminal,
      tooltip: gating.browserMode
        ? 'Terminal (requires desktop app)'
        : 'Toggle Terminal (Ctrl+`)',
      commandId: 'nav:toggle-terminal',
      run: actions.terminal,
    },
    {
      // Rendered by `AiToolbarButton`, which owns its own menu, testid and
      // analytics. One atomic customization slot (F01's recommendation — the
      // AI wave keeps changing what is inside it).
      id: 'ai',
      render: 'custom',
      label: 'AI',
      testId: 'ai-toolbar-button',
      commandId: null,
    },
    {
      id: 'schema-overview',
      render: 'button',
      label: 'Overview',
      testId: 'schema-overview-button',
      icon: LayoutGrid,
      tooltip: gating.schemaOverviewUnsupported
        ? "Schema Overview isn't supported for Azure Data Explorer (kusto) connections"
        : toolbarTooltips.schemaOverview(contextState),
      disabled: gating.schemaOverviewUnsupported,
      className: gating.schemaOverviewUnsupported ? 'opacity-50 cursor-not-allowed' : undefined,
      commandId: 'workspace:schema-overview',
      run: actions['schema-overview'],
    },
    {
      id: 'dashboards',
      render: 'button',
      label: 'Dashboards',
      testId: 'dashboards-button',
      icon: LayoutDashboard,
      tooltip: 'Open Dashboards',
      visible: gating.betaEnabled.dashboards,
      commandId: 'workspace:dashboards',
      run: actions.dashboards,
    },
    {
      // Rendered by `ToolbarMoreMenu`. Anchored last and never hideable —
      // every promoted item stays mirrored inside it (43 e2e spec files open
      // this menu), so hiding it would strand them.
      id: 'more',
      render: 'custom',
      label: 'More',
      testId: 'more-dropdown-button',
      icon: MoreHorizontal,
      tooltip: 'More tools',
      anchor: 'last',
      hideable: false,
      commandId: null,
    },
  ];
}
